import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { type AlertDetailsResponseType } from '@/api/types/Alert'

interface AlertCategoriesFormType {
  notMe: boolean
  authorized?: AlertDetailsResponseType['authorized'] | null
  threat?: AlertDetailsResponseType['threat'] | null
}

const useAlertCategoriesForm = (alertDetails: Partial<AlertDetailsResponseType>) => {
  const defaultValues = useCallback((a: Partial<AlertDetailsResponseType> | null) => ({
    notMe: a?.notMe ?? false,
    authorized: a?.authorized ?? null,
    threat: a?.threat ?? null,
  }), [])

  const validationSchema = useMemo(() => z.object({
    notMe: z.boolean(),
    authorized: z.boolean().nullish(),
    threat: z.boolean().nullish(),
  }).superRefine((data, ctx) => {
    if (!data.notMe) {
      if (data.authorized === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'This field is required',
          path: ['authorized'],
        })
      }
      if (data.threat === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'This field is required',
          path: ['threat'],
        })
      }
    }
  }), [])

  const formDefaults = useMemo(() => defaultValues(alertDetails), [alertDetails, defaultValues])

  const form = useForm<AlertCategoriesFormType>({
    defaultValues: formDefaults,
    resolver: zodResolver(validationSchema),
  })

  const resetForm = useCallback((a: Partial<AlertDetailsResponseType> | null) => {
    form.reset(defaultValues(a))
  }, [form, defaultValues])

  return { form, defaultValues, resetForm }
}

export default useAlertCategoriesForm
